import { api } from '@/lib/api'
import { jwtDecode } from 'jwt-decode'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface User {
  id: string
  email: string
  roles: string[]
  deviceSerial?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  autoLogin: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  const isAuthenticated = !!user

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password })
      const { token, refreshToken } = response.data

      // Store tokens
      localStorage.setItem('token', token)
      localStorage.setItem('refreshToken', refreshToken)

      // Decode and set user
      const decodedToken = jwtDecode<any>(token)
      setUser({
        id: decodedToken.sub,
        email: decodedToken.email,
        roles: decodedToken.roles || [],
        deviceSerial: decodedToken.deviceSerial,
      })

    } catch (error) {
      throw new Error('Login failed')
    }
  }

  const logout = async () => {
    console.log('🚪 AUTH PROVIDER: Logging out...')
    
    // Clear all localStorage items
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenExpires')
    localStorage.removeItem('refreshTokenExpires')
    localStorage.removeItem('deviceId')
    localStorage.removeItem('currentDeviceSerialId')
    localStorage.removeItem('dev_bypass_mode')
    
    // Clear user state
    setUser(null)
    
    // Also clear AuthStore
    try {
      const { useAuthStore } = await import('@/stores/auth-store')
      useAuthStore.getState().userLogout()
    } catch (error) {
      console.error('Error calling AuthStore logout:', error)
    }
    
    console.log('✅ AUTH PROVIDER: Logged out, navigating to login...')
    
    // Navigate to login page
    navigate('/login', { replace: true })
  }

  const autoLogin = async () => {
    console.log('🔐 AUTH PROVIDER: autoLogin started')
    const token = localStorage.getItem('token')
    const isDevBypass = localStorage.getItem('dev_bypass_mode') === 'true'

    console.log('🔐 AUTH PROVIDER: Token found:', !!token)
    console.log('🔐 AUTH PROVIDER: Dev bypass mode:', isDevBypass)

    if (!token) {
      console.log('⚠️ AUTH PROVIDER: No token, skipping autoLogin')
      setIsLoading(false)
      return
    }

    try {
      // For dev bypass mode, create a mock user without JWT decoding
      if (isDevBypass && token.includes('dev-bypass-jwt-token')) {
        console.log('🛠️ AUTH PROVIDER: Dev bypass mode - setting mock user')
        setUser({
          id: 'dev-user-123',
          email: localStorage.getItem('user_email') || 'dev@example.com',
          roles: ['ROLE_ACCESS_CUSTOMERFRONTEND', 'ROLE_ACCESS_ADMINFRONTEND'],
          deviceSerial: 'DEMO123',
        })
        setIsLoading(false)
        console.log('✅ AUTH PROVIDER: User set (dev bypass)')
        return
      }

      // Verify token is still valid for real tokens
      console.log('🔐 AUTH PROVIDER: Decoding JWT token...')
      const decodedToken = jwtDecode<any>(token)
      const isExpired = decodedToken.exp * 1000 < Date.now()

      console.log('🔐 AUTH PROVIDER: Token expired?', isExpired)

      if (isExpired) {
        console.log('⚠️ AUTH PROVIDER: Token expired, logging out')
        logout()
        setIsLoading(false)
        return
      }

      console.log('✅ AUTH PROVIDER: Setting user from token:', decodedToken.sub)
      setUser({
        id: decodedToken.sub,
        email: decodedToken.email,
        roles: decodedToken.roles || [],
        deviceSerial: decodedToken.deviceSerial,
      })
      console.log('✅ AUTH PROVIDER: User set successfully')
    } catch (error) {
      console.error('❌ AUTH PROVIDER: Error in autoLogin:', error)
      logout()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    autoLogin()
  }, [])

  // Initialize devices when user is authenticated
  useEffect(() => {
    console.log('🔄 AUTH PROVIDER: Device init useEffect triggered')
    console.log('🔄 AUTH PROVIDER: user:', user)
    console.log('🔄 AUTH PROVIDER: isAuthenticated:', isAuthenticated)
    
    if (user && isAuthenticated) {
      console.log('✅ AUTH PROVIDER: User authenticated, initializing devices...')
      const initDevices = async () => {
        const { useDevicesStore } = await import('@/stores/devices-store')
        const devicesStore = useDevicesStore.getState()
        try {
          await devicesStore.initializeDevices()
          console.log('✅ AUTH PROVIDER: Devices initialized successfully')
        } catch (error) {
          console.error('❌ AUTH PROVIDER: Failed to initialize devices:', error)
        }
      }
      
      initDevices()
    } else {
      console.log('⚠️ AUTH PROVIDER: Not authenticated, skipping device initialization')
    }
  }, [user, isAuthenticated])

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    autoLogin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
